import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
} from "react";
import {
  BRAND_ROLES,
  CAPABILITY_LABELS,
  CAPABILITY_MATRIX_ROWS,
  ROLE_LABELS,
  ROLE_PURPOSE,
  ROLE_SCOPE,
  roleHasCapability,
  type AnyRole,
  type BrandRole,
  type InvitationSummary,
  type MembershipSummary,
  type Paginated,
} from "@velivo/shared";
import { Field, SelectField } from "../components/Field";
import { EmptyState } from "../components/EmptyState";
import { Icon } from "../components/Icon";
import { Tabs } from "../components/Tabs";
import { useAuth } from "../auth/AuthContext";
import { api, ApiRequestError } from "../api/client";
import "./people.css";

/**
 * People — FR-ADMIN-02/03, the one screen where access is granted, changed and
 * removed at BOTH levels of the hierarchy.
 *
 * It replaces a Team screen that could only invite Brand roles. That screen was
 * the direct cause of a real confusion: the Organization Admin role existed in
 * the API, in the token, and in the docs, but there was nowhere in the product
 * to see one or create one — so the only admin anyone ever met was a Brand
 * Admin, and the two names blurred into each other.
 *
 * The fix is structural rather than cosmetic. The two scopes are separate tabs
 * because they are separate grants: an Organization Admin grant is not "a bigger
 * Brand Admin", it lives one level up and reaches every Brand automatically
 * (RBAC_MODEL.md §4). The reference tab renders the permission matrix from the
 * shared capability table the middleware itself uses, so what a customer is
 * told matches what the server does, permanently.
 */
export function PeoplePage() {
  const { user, activeBrand, activeOrganization, can, canOrg } = useAuth();

  if (!activeBrand) {
    return (
      <>
        <header className="page__header">
          <h1 className="page__title">People</h1>
        </header>
        <div className="card">
          <EmptyState
            icon="brand"
            title="No Brand selected"
            description="Choose a Brand from the switcher in the sidebar to manage who has access to it."
          />
        </div>
      </>
    );
  }

  const organizationId = activeBrand.organizationId;
  const mayManageBrand = can("brand:manage_members");
  const mayManageOrg = canOrg("org:manage_members");

  const tabs = [
    {
      id: "brand",
      label: "Brand access",
      icon: "users" as const,
      render: () => (
        <BrandAccessTab
          brandId={activeBrand.brandId}
          brandName={activeBrand.brandName}
          mayManage={mayManageBrand}
          currentUserId={user?.userId ?? ""}
        />
      ),
    },
    ...(mayManageOrg
      ? [
          {
            id: "organization",
            label: "Organization admins",
            icon: "organization" as const,
            render: () => (
              <OrganizationAdminsTab
                organizationId={organizationId}
                organizationName={
                  activeOrganization?.organizationName ?? "this Organization"
                }
                currentUserId={user?.userId ?? ""}
              />
            ),
          },
          {
            id: "invitations",
            label: "Pending invitations",
            icon: "mail" as const,
            render: () => <InvitationsTab organizationId={organizationId} />,
          },
        ]
      : []),
    {
      id: "roles",
      label: "Who can do what",
      icon: "shield" as const,
      render: () => <RoleReferenceTab />,
    },
  ];

  return (
    <>
      <header className="page__header">
        <h1 className="page__title">People</h1>
        <p className="page__subtitle">
          Access is granted at two levels: the Organization, and each Brand
          inside it.
        </p>
      </header>
      <Tabs tabs={tabs} ariaLabel="People sections" />
    </>
  );
}

/* ------------------------------------------------------------------ brand */

function BrandAccessTab({
  brandId,
  brandName,
  mayManage,
  currentUserId,
}: {
  brandId: string;
  brandName: string;
  mayManage: boolean;
  currentUserId: string;
}) {
  const { refreshUser } = useAuth();
  const [members, setMembers] = useState<MembershipSummary[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<BrandRole>("CommsManager");
  const [submitting, setSubmitting] = useState(false);
  const [busyUserId, setBusyUserId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const page = await api<Paginated<MembershipSummary>>(
        `/brands/${brandId}/memberships`,
      );
      setMembers(page.items);
    } catch (caught) {
      setMembers([]);
      setLoadError(
        caught instanceof ApiRequestError
          ? caught.message
          : "Could not load the member list.",
      );
    }
  }, [brandId]);

  useEffect(() => {
    void load();
  }, [load]);

  function report(caught: unknown, fallback: string) {
    setError(caught instanceof ApiRequestError ? caught.message : fallback);
  }

  async function handleInvite(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setNotice(null);
    setSubmitting(true);
    try {
      const result = await api<{ status: "granted" | "invited" }>(
        `/brands/${brandId}/memberships`,
        { method: "POST", body: { email, role } },
      );
      setNotice(
        result.status === "invited"
          ? `Invitation sent to ${email}. It expires in 7 days.`
          : `${email} now has the ${ROLE_LABELS[role]} role on this Brand.`,
      );
      setEmail("");
      await load();
    } catch (caught) {
      report(caught, "Could not send that invitation.");
    } finally {
      setSubmitting(false);
    }
  }

  async function changeRole(member: MembershipSummary, next: BrandRole) {
    setError(null);
    setNotice(null);
    setBusyUserId(member.userId);
    try {
      await api(`/brands/${brandId}/memberships/${member.userId}`, {
        method: "PATCH",
        body: { role: next },
      });
      setNotice(`${member.email} is now ${ROLE_LABELS[next]} on this Brand.`);
      await load();
    } catch (caught) {
      report(caught, "Could not change that role.");
    } finally {
      setBusyUserId(null);
    }
  }

  async function removeMember(member: MembershipSummary) {
    if (
      !window.confirm(
        `Remove ${member.email} from ${brandName}? They keep their account but lose access to this Brand.`,
      )
    ) {
      return;
    }
    setError(null);
    setNotice(null);
    setBusyUserId(member.userId);
    try {
      await api(`/brands/${brandId}/memberships/${member.userId}`, {
        method: "DELETE",
      });
      setNotice(`${member.email} no longer has access to this Brand.`);
      await load();
      await refreshUser();
    } catch (caught) {
      report(caught, "Could not remove that person.");
    } finally {
      setBusyUserId(null);
    }
  }

  return (
    <>
      <p className="people__lede">
        Everyone listed here can reach <strong>{brandName}</strong> and nothing
        else in the Organization — unless their access comes from an
        Organization Admin grant, which reaches every Brand.
      </p>

      {notice && (
        <div className="alert alert--success" role="status">
          {notice}
        </div>
      )}
      {error && (
        <div className="alert alert--error" role="alert">
          {error}
        </div>
      )}

      {mayManage && (
        <div className="card people__invite">
          <h2 className="people__cardtitle">
            Give someone access to this Brand
          </h2>
          <form onSubmit={handleInvite} noValidate>
            <div className="people__inviterow">
              <Field
                label="Email"
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <SelectField
                label="Role"
                value={role}
                onChange={(e) => setRole(e.target.value as BrandRole)}
              >
                {BRAND_ROLES.map((option) => (
                  <option key={option} value={option}>
                    {ROLE_LABELS[option]}
                  </option>
                ))}
              </SelectField>
            </div>
            <p className="people__rolehelp">{ROLE_PURPOSE[role]}</p>
            <div className="people__formactions">
              <button
                type="submit"
                className="btn btn--primary"
                disabled={submitting}
              >
                {submitting ? "Sending…" : "Send invitation"}
              </button>
            </div>
          </form>
        </div>
      )}

      <MemberTable
        members={members}
        loadError={loadError}
        emptyDescription={
          mayManage
            ? "Invite a teammate above to give them a role on this Brand."
            : "A Brand Admin or Organization Admin can invite teammates to this Brand."
        }
        renderRole={(member) => {
          // An inherited row has no BrandMembership to edit — the grant lives at
          // the Organization (RBAC_MODEL.md §4). Offering a role dropdown here
          // would imply a change this endpoint cannot make; the honest control
          // is a pointer to where the grant actually is.
          if (member.inherited) {
            return (
              <div className="people__inherited">
                <span className="badge badge--info">
                  {ROLE_LABELS[member.role]}
                </span>
                <span className="people__inheritednote">
                  via Organization Admin — manage on the Organization tab
                </span>
              </div>
            );
          }
          if (!mayManage) {
            return (
              <span className="badge badge--accent">
                {ROLE_LABELS[member.role]}
              </span>
            );
          }
          const isSelf = member.userId === currentUserId;
          return (
            <div className="people__roleedit">
              <label
                className="visually-hidden"
                htmlFor={`role-${member.userId}`}
              >
                Role for {member.email}
              </label>
              <select
                id={`role-${member.userId}`}
                className="field__select people__roleselect"
                value={member.role}
                disabled={isSelf || busyUserId === member.userId}
                onChange={(e) =>
                  void changeRole(member, e.target.value as BrandRole)
                }
              >
                {BRAND_ROLES.map((option) => (
                  <option key={option} value={option}>
                    {ROLE_LABELS[option]}
                  </option>
                ))}
              </select>
              {isSelf && (
                <span className="people__selfnote">your own access</span>
              )}
            </div>
          );
        }}
        renderActions={
          mayManage
            ? (member) =>
                member.inherited || member.userId === currentUserId ? null : (
                  <button
                    type="button"
                    className="btn btn--ghost btn--destructive-text btn--sm"
                    disabled={busyUserId === member.userId}
                    onClick={() => void removeMember(member)}
                  >
                    Remove
                  </button>
                )
            : undefined
        }
      />

      {mayManage && (
        <p className="people__footnote">
          <Icon name="info" size={16} /> You cannot change or remove your own
          access — another administrator has to do that. It keeps a Brand from
          being left with no admin, and means every grant has a second person
          behind it in the audit log.
        </p>
      )}
    </>
  );
}

/* ----------------------------------------------------------- organization */

function OrganizationAdminsTab({
  organizationId,
  organizationName,
  currentUserId,
}: {
  organizationId: string;
  organizationName: string;
  currentUserId: string;
}) {
  const [members, setMembers] = useState<MembershipSummary[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [busyUserId, setBusyUserId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const page = await api<Paginated<MembershipSummary>>(
        `/organizations/${organizationId}/memberships`,
      );
      setMembers(page.items);
    } catch (caught) {
      setMembers([]);
      setLoadError(
        caught instanceof ApiRequestError
          ? caught.message
          : "Could not load the Organization Admin list.",
      );
    }
  }, [organizationId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleInvite(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setNotice(null);
    setSubmitting(true);
    try {
      const result = await api<{ status: "granted" | "invited" }>(
        `/organizations/${organizationId}/memberships`,
        { method: "POST", body: { email, role: "OrganizationAdmin" } },
      );
      setNotice(
        result.status === "invited"
          ? `Invitation sent to ${email}. It expires in 7 days.`
          : `${email} is now an Organization Admin of ${organizationName}.`,
      );
      setEmail("");
      await load();
    } catch (caught) {
      setError(
        caught instanceof ApiRequestError
          ? caught.message
          : "Could not send that invitation.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function removeAdmin(member: MembershipSummary) {
    if (
      !window.confirm(
        `Remove ${member.email} as an Organization Admin? They lose access to every Brand in ${organizationName}.`,
      )
    ) {
      return;
    }
    setError(null);
    setNotice(null);
    setBusyUserId(member.userId);
    try {
      await api(
        `/organizations/${organizationId}/memberships/${member.userId}`,
        {
          method: "DELETE",
        },
      );
      setNotice(`${member.email} is no longer an Organization Admin.`);
      await load();
    } catch (caught) {
      setError(
        caught instanceof ApiRequestError
          ? caught.message
          : "Could not remove that administrator.",
      );
    } finally {
      setBusyUserId(null);
    }
  }

  return (
    <>
      <p className="people__lede">
        An Organization Admin owns <strong>{organizationName}</strong> itself:
        billing, creating Brands, and full Brand Admin access to{" "}
        <strong>every</strong> Brand under it — including Brands created after
        they were added. Grant it sparingly; for someone who only works on one
        Brand, use the Brand access tab instead.
      </p>

      {notice && (
        <div className="alert alert--success" role="status">
          {notice}
        </div>
      )}
      {error && (
        <div className="alert alert--error" role="alert">
          {error}
        </div>
      )}

      <div className="card people__invite">
        <h2 className="people__cardtitle">Add an Organization Admin</h2>
        <form onSubmit={handleInvite} noValidate>
          <div className="people__inviterow">
            <Field
              label="Email"
              type="email"
              name="orgAdminEmail"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="people__formactions">
            <button
              type="submit"
              className="btn btn--primary"
              disabled={submitting}
            >
              {submitting ? "Sending…" : "Send invitation"}
            </button>
          </div>
        </form>
      </div>

      <MemberTable
        members={members}
        loadError={loadError}
        emptyDescription="Add an Organization Admin above."
        renderRole={() => (
          <span className="badge badge--accent">Organization Admin</span>
        )}
        renderActions={(member) =>
          member.userId === currentUserId ? (
            <span className="people__selfnote">your own access</span>
          ) : (
            <button
              type="button"
              className="btn btn--ghost btn--destructive-text btn--sm"
              disabled={busyUserId === member.userId}
              onClick={() => void removeAdmin(member)}
            >
              Remove
            </button>
          )
        }
      />
    </>
  );
}

/* ----------------------------------------------------------- invitations */

function InvitationsTab({ organizationId }: { organizationId: string }) {
  const [rows, setRows] = useState<InvitationSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const page = await api<Paginated<InvitationSummary>>(
        `/organizations/${organizationId}/invitations`,
      );
      setRows(page.items);
    } catch (caught) {
      setRows([]);
      setError(
        caught instanceof ApiRequestError
          ? caught.message
          : "Could not load pending invitations.",
      );
    }
  }, [organizationId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function revoke(invitation: InvitationSummary) {
    setBusy(invitation.invitationId);
    try {
      await api(`/invitations/${invitation.invitationId}`, {
        method: "DELETE",
      });
      await load();
    } catch (caught) {
      setError(
        caught instanceof ApiRequestError
          ? caught.message
          : "Could not revoke that invitation.",
      );
    } finally {
      setBusy(null);
    }
  }

  const pending = useMemo(
    () => (rows ?? []).filter((row) => row.status === "pending"),
    [rows],
  );

  if (rows === null) return <TableSkeleton />;

  return (
    <>
      <p className="people__lede">
        An invitation grants nothing until it is accepted — the membership is
        created at that moment, and the inviter&apos;s permission is re-checked
        then, so a revoked administrator&apos;s outstanding invitations cannot
        still let someone in.
      </p>

      {error && (
        <div className="alert alert--error" role="alert">
          {error}
        </div>
      )}

      <div className="card people__flushcard">
        {pending.length === 0 ? (
          <EmptyState
            icon="mail"
            title="No invitations waiting"
            description="Everyone who has been invited has already accepted."
          />
        ) : (
          <div className="people__tablewrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Email</th>
                  <th scope="col">Grants</th>
                  <th scope="col">Expires</th>
                  <th scope="col" className="table__actions">
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {pending.map((row) => (
                  <tr key={row.invitationId}>
                    <td>{row.email}</td>
                    <td>
                      <span className="badge badge--accent">
                        {ROLE_LABELS[row.role]}
                      </span>{" "}
                      <span className="people__scopenote">
                        {row.brandId
                          ? `on ${row.brandName ?? "a Brand"}`
                          : "Organization-wide"}
                      </span>
                    </td>
                    <td>{new Date(row.expiresAt).toLocaleDateString()}</td>
                    <td className="table__actions">
                      <button
                        type="button"
                        className="btn btn--ghost btn--destructive-text btn--sm"
                        disabled={busy === row.invitationId}
                        onClick={() => void revoke(row)}
                      >
                        Revoke
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

/* ------------------------------------------------------------- reference */

const ALL_ROLES: AnyRole[] = [
  "OrganizationAdmin",
  "BrandAdmin",
  "CommsManager",
  "IRLead",
  "CrisisLead",
  "ExecutiveViewer",
];

function RoleReferenceTab() {
  return (
    <>
      <p className="people__lede">
        Every role is granted at one of two levels. Organization Admin is
        granted on the Organization and reaches all of its Brands; the other
        five are granted on a single Brand and reach only that one.
      </p>

      <div className="people__rolecards">
        {ALL_ROLES.map((role) => (
          <div key={role} className="card people__rolecard">
            <div className="people__rolecardhead">
              <h3 className="people__rolename">{ROLE_LABELS[role]}</h3>
              <span
                className={`badge ${ROLE_SCOPE[role] === "organization" ? "badge--accent" : "badge--neutral"}`}
              >
                {ROLE_SCOPE[role] === "organization"
                  ? "Organization"
                  : "One Brand"}
              </span>
            </div>
            <p className="people__rolepurpose">{ROLE_PURPOSE[role]}</p>
          </div>
        ))}
      </div>

      <div className="card people__flushcard">
        <div className="people__tablewrap">
          <table className="table people__matrix">
            <caption className="visually-hidden">
              Permissions held by each role. Organization Admin&apos;s
              Brand-level permissions are inherited on every Brand in the
              Organization.
            </caption>
            <thead>
              <tr>
                <th scope="col">Can…</th>
                {ALL_ROLES.map((role) => (
                  <th key={role} scope="col" className="people__matrixhead">
                    {ROLE_LABELS[role]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {CAPABILITY_MATRIX_ROWS.map((capability) => (
                <tr key={capability}>
                  <th scope="row" className="people__matrixrow">
                    {CAPABILITY_LABELS[capability]}
                    {capability.startsWith("org:") && (
                      <span className="people__scopetag">
                        Organization-level
                      </span>
                    )}
                  </th>
                  {ALL_ROLES.map((role) => {
                    const held = roleHasCapability(role, capability);
                    const inherited =
                      held &&
                      role === "OrganizationAdmin" &&
                      capability.startsWith("brand:");
                    return (
                      <td key={role} className="people__matrixcell">
                        {held ? (
                          <>
                            {/* Colour is never the only signal — the icon and
                                the accessible name both carry it. */}
                            <Icon
                              name="check"
                              size={16}
                              className="people__yes"
                            />
                            <span className="visually-hidden">
                              {inherited
                                ? "Yes, inherited on every Brand"
                                : "Yes"}
                            </span>
                            {inherited && (
                              <span className="people__inh">every Brand</span>
                            )}
                          </>
                        ) : (
                          <>
                            <span aria-hidden="true" className="people__no">
                              —
                            </span>
                            <span className="visually-hidden">No</span>
                          </>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <p className="people__footnote">
        <Icon name="shield" size={16} /> These permissions are enforced on the
        server for every request. Hiding a button is a courtesy; the API refuses
        the call regardless of what the screen shows.
      </p>
    </>
  );
}

/* ---------------------------------------------------------------- shared */

function TableSkeleton() {
  return (
    <div className="card">
      <div
        className="skeleton"
        style={{ height: 20, marginBottom: "var(--space-3)" }}
      />
      <div
        className="skeleton"
        style={{ height: 20, marginBottom: "var(--space-3)" }}
      />
      <div className="skeleton" style={{ height: 20 }} />
    </div>
  );
}

function MemberTable({
  members,
  loadError,
  emptyDescription,
  renderRole,
  renderActions,
}: {
  members: MembershipSummary[] | null;
  loadError: string | null;
  emptyDescription: string;
  renderRole: (member: MembershipSummary) => React.ReactNode;
  renderActions?: (member: MembershipSummary) => React.ReactNode;
}) {
  if (members === null) return <TableSkeleton />;

  if (loadError) {
    return (
      <div className="card">
        <div className="alert alert--error" role="alert">
          {loadError}
        </div>
      </div>
    );
  }

  return (
    <div className="card people__flushcard">
      {members.length === 0 ? (
        <EmptyState
          icon="users"
          title="Nobody here yet"
          description={emptyDescription}
        />
      ) : (
        <div className="people__tablewrap">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Email</th>
                <th scope="col">Role</th>
                {renderActions && (
                  <th scope="col" className="table__actions">
                    <span className="visually-hidden">Actions</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {members.map((member) => (
                <tr key={`${member.userId}-${member.inherited}`}>
                  <td>{member.name || "—"}</td>
                  <td>{member.email}</td>
                  <td>{renderRole(member)}</td>
                  {renderActions && (
                    <td className="table__actions">{renderActions(member)}</td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
